<script setup lang="ts">
import { __ } from '@@/bits/intl'
import OzIconButton, {
  OzIconButtonColorScheme,
  OzIconButtonSizePreset,
} from '@@/library/v4/components/OzIconButton.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import OzScreenSizeAwareCenterModal from '@@/library/v4/components/OzScreenSizeAwareCenterModal.vue'
import { AccessibilitySetting } from '@@/pinia/accessibility_settings_store'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { User, UserCamelCase } from '@@/types'
import AccessibilitySettings from '@@/vuecomponents/AccessibilitySettings.vue'
import { Portal } from '@linusborg/vue-simple-portal'

const windowSizeStore = useWindowSizeStore()

withDefaults(
  defineProps<{
    visible: boolean
    portalSelector: string
    user?: User | UserCamelCase
    updateType?: 'page' | 'global'
    settingsToShow?: Array<AccessibilitySetting>
    darkMode?: boolean | 'auto'
  }>(),
  {
    user: undefined,
    updateType: 'page',
    settingsToShow: () => [
      AccessibilitySetting.KeyboardShortcuts,
      AccessibilitySetting.AppMessagesAutoDismiss,
      AccessibilitySetting.HighContrastMode,
      AccessibilitySetting.ReducedMotion,
    ],
    darkMode: 'auto',
  },
)

defineEmits(['close'])
</script>
<template>
  <Portal :selector="portalSelector">
    <OzScreenSizeAwareCenterModal
      v-if="visible"
      role="dialog"
      :aria-labelledby="`accessibility-settings-title`"
      :window-height="windowSizeStore.windowHeight"
      :width-class="'w-135'"
      :is-phone="windowSizeStore.isSmallerThanTabletPortrait"
      :full-size="false"
      :is-draggable-drawer="true"
      :disable-drag="true"
      :dark-mode="darkMode"
      data-testid="accessibilitySettingsModal"
      @scrim-click="$emit('close')"
      @scrim-esc="$emit('close')"
    >
      <template #header>
        <div
          class="flex justify-between items-center my-2 @tablet-portrait/surface-container:m-4 tablet-portrait:-@:m-4"
        >
          <div class="flex-1 flex justify-start">
            <OzPlainButton
              v-if="windowSizeStore.isSmallerThanTabletPortrait"
              class="!px-4"
              :aria-label="__('Close accessibility settings dialog')"
              :color-scheme="OzPlainButtonColorScheme.Secondary"
              :dark-mode="darkMode"
              :size-preset="OzPlainButtonSizePreset.H40px"
              :text="__('Close')"
              @click="$emit('close')"
            />
            <OzIconButton
              v-else
              :label="__('Close accessibility settings dialog')"
              :color-scheme="OzIconButtonColorScheme.Secondary"
              :dark-mode="darkMode"
              :size-preset="OzIconButtonSizePreset.H32px"
              icon-name="close_small"
              data-testid="accessibilitySettingsModalCloseButton"
              @click="$emit('close')"
            />
          </div>
          <div class="flex justify-center">
            <h2
              id="accessibility-settings-title"
              :class="[
                'text-heading-3 font-semibold',
                { 'dark:text-light-text-100 text-dark-text-100': darkMode === 'auto' },
                { 'text-light-text-100': darkMode === true },
                { 'text-dark-text-100': darkMode === false },
              ]"
            >
              {{ __('Accessibility settings') }}
            </h2>
          </div>
          <div class="flex-1 flex justify-end"></div>
        </div>
      </template>
      <template #body>
        <div class="px-4 pb-4">
          <AccessibilitySettings
            :user="user"
            :update-type="updateType"
            :settings-to-show="settingsToShow"
            :dark-mode="darkMode"
          />
        </div>
      </template>
    </OzScreenSizeAwareCenterModal>
  </Portal>
</template>
